import { useEffect, useRef } from 'react';
import type { SoundManager } from './sound';

/**
 * A game's own recorded sounds (ElevenLabs Sound Effects, served from /sfx/<game>/<name>.mp3), played from the
 * events the game already reports to the screens (its view's fx list: items with increasing ids, kept for about
 * a second). Each game supplies a `pick` that turns an event into a sound: `mine` (it happened to, or was done
 * by, this phone's player) plays on that phone; anything else is a room sound (the TV when there is one, every
 * phone otherwise). The first render only remembers where the list is, so joining mid-round replays nothing.
 */
export const gameSfx = (game: string, names: readonly string[]): Record<string, string> =>
  Object.fromEntries(names.map((n) => [`${game}.${n}`, `/sfx/${game}/${n}.mp3`]));

export interface PickedSound {
  /** `<game>.<name>`, a key of the game's gameSfx map. */
  id: string;
  mine?: boolean;
  level?: number;
}

export function useFxSounds<E extends { id: number }>(opts: {
  sound: SoundManager;
  files: Record<string, string>;
  fx: readonly E[] | null | undefined;
  /** The TV (or a watcher): nothing is "mine", everything is a room sound. */
  tv?: boolean;
  pick: (e: E) => PickedSound | null;
}) {
  const { sound, files, fx, tv = false, pick } = opts;
  useEffect(() => sound.loadGameSounds(files), [sound, files]);
  const last = useRef(-1);
  const pickRef = useRef(pick);
  pickRef.current = pick;
  useEffect(() => {
    if (!fx) return;
    const max = fx.reduce((m, e) => Math.max(m, e.id), 0);
    // First look, or the ids started again (a new round or game): note where we are and play nothing old.
    if (last.current < 0 || max < last.current) {
      last.current = max;
      return;
    }
    for (const e of fx) {
      if (e.id <= last.current) continue;
      const p = pickRef.current(e);
      if (!p) continue;
      const mine = !tv && !!p.mine;
      sound.playGameSound(p.id, { scope: mine ? 'personal' : 'room', level: p.level ?? (mine ? 0.95 : 0.7) });
    }
    last.current = Math.max(last.current, max);
  }, [fx, sound, tv]);
}

/** Plays a sound once for each new id in a list (meteor warnings, barks…): same first-look rule as useFxSounds. */
export function useNewIdSounds<E extends { id: number }>(sound: SoundManager, list: readonly E[] | null | undefined, pick: (e: E) => PickedSound | null) {
  const seen = useRef<Set<number> | null>(null);
  const pickRef = useRef(pick);
  pickRef.current = pick;
  useEffect(() => {
    if (!list) return;
    if (!seen.current) {
      seen.current = new Set(list.map((e) => e.id));
      return;
    }
    const next = new Set<number>();
    for (const e of list) {
      next.add(e.id);
      if (seen.current.has(e.id)) continue;
      const p = pickRef.current(e);
      if (p) sound.playGameSound(p.id, { scope: 'room', level: p.level ?? 0.6 });
    }
    seen.current = next;
  }, [list, sound]);
}
